import React, { useContext, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IconPack, library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { posthog } from 'posthog-js';
// import widgetApiInterface from '@kemu-io/kemu-core/common/widgetApi';
import canvasManager from '@kemu-io/kemu-core/common/managers/canvasManager.js';
import kemuCore from '@kemu-io/kemu-core';
import eventHistory from '@kemu-io/kemu-core/common/eventHistory';
import * as recipeCache from '@kemu-io/kemu-core/common/recipeCache';
// import { ConfigProvider } from 'antd';
// import ConfigProvider from 'antd/lib/config-provider';
// import AntApp from 'antd/lib/app';
import { CanvasInstance } from '@kemu-io/kemu-core/types';
import { variablesManager } from '@kemu-io/kemu-core/common';
import styles from './App.module.css';
import I18nProvider from './assets/i18n/I18nProvider';
import * as serviceWorker from './serviceWorker';
import { setNewVersionAvailable } from './features/interface/interfaceSlice';
// import Hotspots from './@asurantech/hotspots/Hotspots';
import * as widgetApi from './api/widget/widgetApi';
import khConnectionManager from './app/kemuHub/connectionManager';
import PageLayout from './features/PageLayout/PageLayout';
// Clerk-based authentication components
import ClerkSignIn from './features/auth/login/ClerkSignIn';
import ClerkSignUp from './features/auth/signup/ClerkSignUp';
// import ClerkCustomSignUp from './features/auth/signup/ClerkCustomSignUp';
// import AccountDetails from './features/auth/AccountDetails/AccountDetails';
import ErrorPage from './features/ErrorPage/ErrorPage';
import ImportRecipe from './features/directAction/importRecipe/importRecipe';
import ImportWidgetScreen from './features/directAction/importWidget/importWidget';
import AuthStateRouter from './features/AuthStateRouter/AuthStateRouter';
import UserSessionContextProvider from '@common/context/UserSessionContext/UserSessionContext';
import globals from '@common/globals';
import routes from '@common/routes/index';
import loadSpeedyVision from '@common/speedy-vision/speedyVision';
// import configureAmplify from '@common/amplify';
import configureClerk, { clerkConfig } from '@common/clerkAuth';
import { ClerkProvider } from '@clerk/clerk-react';
import Logger from '@common/logger';
import usePreventNavigation from '@hooks/usePreventNavigation';
import ApplicationContextProvider, { ApplicationContext } from '@common/context/ApplicationContext/ApplicationContext';
import ThemeProvider from '@common/theme/ThemeProvider';
import useHasPosthogFeature from '@hooks/useHasPosthogFeature';
import MaintenanceScreen from './features/MaintenanceScreen/MaintenanceScreen';
import globalVariablesManager from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { BinaryFile } from '@kemu-io/hs-types';
import { isBinaryFile } from '@kemu-io/kemu-core/common/utils';

const logger = Logger('App');

const allowedRedirectOrigins: string[] = [
  // globals.USER_API
];

// Temporarily keeping both auth systems during migration
// configureAmplify();
configureClerk();

posthog.init(
  globals.POSTHOG_PROJECT_API_TOKEN,
  {
    defaults: '2025-05-24',
    api_host: 'https://us.i.posthog.com',
    autocapture: false,
    'opt_in_site_apps': true,
    person_profiles: 'identified_only',
    enable_recording_console_log: true,
  }
);

if (globals.STAGE === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__kd__ = {
    core: kemuCore,
    recipeCache,
    eventHistory,
    globalVariablesManager,
    variablesManager,
    getThings: (index?: number) => {
      const allRecipes = recipeCache.getAllRecipes();
      const keys = Object.keys(allRecipes);
      const firstRecipe = allRecipes[keys[0]];
      const things = firstRecipe.blocks;
      const thingsKeys = Object.keys(things);
      const thing = index !== undefined ? things[thingsKeys[index]] : undefined;

      // console.log(thing ?? things);

      if (thing) {
        return { ...thing };
      } else {
        return { ...things };
      }
    },
    /** paints a table with all the widgets and their children */
    recipeTree: () => {
      const allRecipes = recipeCache.getAllRecipes();
      const firstRecipe = allRecipes[Object.keys(allRecipes)[0]];
      const things = firstRecipe.blocks;
      const thingsKeys = Object.keys(things);
      const thing = things[thingsKeys[0]];
      if (!thing) { return null; }

      const widgets = thing.gates;
      const simpleWidgets = Object.values(widgets).map((widget) => ({
        id: widget.id,
        type: widget.type,
        children: widget.children.map((child) => child.sourcePort + ' -> ' + child.targetPort + ' ' + child.childId).join('\n'),
      }));

      (simpleWidgets as any).table = () => {
        return console.table(simpleWidgets);
      };

      return simpleWidgets;
    }
  };
}

canvasManager.setManager({
  createCanvas: (width: number, height: number) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas as unknown as CanvasInstance;
  },
  loadImage: (data: Uint8ClampedArray | Uint8Array | ArrayBuffer | Buffer | string | BinaryFile) => {
    const isBinaryFileType = isBinaryFile(data);

    if (isBinaryFileType) {
      return new Promise((resolve, reject) => {
        const binaryFile = data as BinaryFile;
        
        // Validate that the format is an image type
        if (!binaryFile.format || !binaryFile.format.startsWith('image/')) {
          reject(new Error(`Unsupported file format: ${binaryFile.format || 'unknown'}. Expected an image format (e.g., image/png, image/jpeg).`));
          return;
        }

        // List of supported image MIME types
        const supportedImageTypes = [
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/gif',
          'image/webp',
          'image/bmp',
          'image/svg+xml'
        ];

        // Check if the format is in the supported list
        if (!supportedImageTypes.includes(binaryFile.format.toLowerCase())) {
          reject(new Error(`Unsupported image format: ${binaryFile.format}. Supported formats are: ${supportedImageTypes.join(', ')}.`));
          return;
        }

        // Convert binary data to ArrayBuffer if needed
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const binaryData: any = binaryFile.data;
        
        // Normalize to Uint8Array first, then extract ArrayBuffer
        // This ensures we always get a proper ArrayBuffer regardless of input type
        let uint8Array: Uint8Array;
        if (binaryData instanceof ArrayBuffer) {
          uint8Array = new Uint8Array(binaryData);
        } else if (binaryData instanceof Uint8Array) {
          uint8Array = binaryData;
        } else if (binaryData instanceof Uint8ClampedArray) {
          uint8Array = new Uint8Array(binaryData);
        } else if (binaryData && typeof binaryData.buffer === 'object' && binaryData.buffer instanceof ArrayBuffer) {
          // For Buffer type (Node.js) or typed arrays with a buffer property
          uint8Array = new Uint8Array(binaryData.buffer);
        } else {
          // Fallback: try to create Uint8Array from the data
          uint8Array = new Uint8Array(binaryData);
        }
        
        // Get the ArrayBuffer from the Uint8Array
        // When creating a new Uint8Array, the buffer is always an ArrayBuffer (not SharedArrayBuffer)
        const arrayBuffer = uint8Array.buffer as ArrayBuffer;

        // Create a Blob from the binary data with the correct MIME type
        const blob = new Blob([arrayBuffer], { type: binaryFile.format });
        const imageUrl = URL.createObjectURL(blob);

        // Create an Image element and load the blob URL
        const img = new Image();
        
        img.onload = () => {
          // Clean up the object URL
          URL.revokeObjectURL(imageUrl);
          
          // Create canvas and convert to ImageData
          const canvas = canvasManager.createCanvas(img.width, img.height);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, img.width, img.height);
          resolve(imageData);
        };

        img.onerror = () => {
          // Clean up the object URL
          URL.revokeObjectURL(imageUrl);
          reject(new Error(`Failed to load image from binary data. The file may be corrupted or the format may not be valid ${binaryFile.format}.`));
        };

        img.src = imageUrl;
      });
    }

    const img = new Image();
    if (typeof data === 'string') {
      img.src = data;
    } else {
      img.src = URL.createObjectURL(new Blob([data as ArrayBuffer]));
    }

    return new Promise((resolve) => {
      img.onload = () => {
        const canvas = canvasManager.createCanvas(img.width, img.height);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        resolve(imageData);
      };
    });
  }
});


const App: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const { language } = useContext(ApplicationContext);
  const [maintenanceEnabled, maintenanceReady, maintenanceBypass] = useHasPosthogFeature<{ code: string }>('site-will-be-back-on');
  // Ready the querystring param to bypass the maintenance screen
  const queryString = new URLSearchParams(window.location.search);
  const bypass = queryString.get('maintenance-bypass');
  const bypassMaintenance = bypass && bypass === maintenanceBypass?.code;
  
  // const abortLoading = globals.STAGE !== 'production';
  const initializedRef = useRef(false);
  // useUserbackWidget(abortLoading);
  usePreventNavigation();
  loadSpeedyVision();

  useEffect(() => {
    // The store provider seems to be causing App.tsx to be rendered twice.
    if (initializedRef.current) { return; }

    initializedRef.current = true;
    // This exports the whole font-awesome icon packs for Brand and Solid.
    library.add(fab as IconPack, fas as IconPack);
    // khConnectionManager.start();
    kemuCore.setHubConnector(khConnectionManager.getConnector());
    kemuCore.enableEventTracking();
    // Set the actual (authenticated) api so that kemu-core can fetch block information.
    // thingApiInterface.setApi(thingApi);
    // widgetApiInterface.setApi(widgetApi);
    // WebServiceManager.setServiceManager(WebServiceType.Http, buildHttpService());
    // WebServiceManager.setServiceManager(WebServiceType.DynamoDb, buildDynamoDbService());
    // WebServiceManager.setServiceManager(WebServiceType.GoogleSheets, buildGoogleSheetsService());

    logger.log('Registering service worker');
    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://bit.ly/CRA-PWA
    // serviceWorker.unregister();
    serviceWorker.register({
      onUpdate: (registration) => {
        logger.log('Skipping waiting to use new version: ');
        registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
      },

      onInstalling: (state) => {
        // STATES:
        // 'installed'
        // => version skipped after the above message
        // 'activating'
        // 'activated' <== Show popup to user?
        logger.log('Service worker state: ', state);
        if (state === 'activated') {
          dispatch(setNewVersionAvailable());
        }
      }
    });
  }, [dispatch]);

  // Maintenance screen gated by PostHog flag
  if (maintenanceReady && maintenanceEnabled && globals.STAGE === 'production' && !bypassMaintenance) {
    return (
      <I18nProvider language={ language }>
        <MaintenanceScreen />
      </I18nProvider>
    );
  }

  // Wait for the feature flag to be ready before rendering anything else
  if(!maintenanceReady && globals.STAGE === 'production') { return null; }

  return (
    <div className={styles.Page}>
      <ThemeProvider>
      {/* <AntApp> */}
        <ClerkProvider 
          allowedRedirectOrigins={allowedRedirectOrigins}
          publishableKey={clerkConfig.publishableKey}
          signInUrl={clerkConfig.signInUrl}
          signUpUrl={clerkConfig.signUpUrl}
        >
          <Router>
            <ApplicationContextProvider>
              <I18nProvider language={ language }>
                {/* <Hotspots /> */}
                <UserSessionContextProvider>
                  <Routes>
                    <Route path={routes.getBaseRoute()} element={<PageLayout />} errorElement={<ErrorPage />}>

                      {/* Authentication Routes - Toggle between legacy and Clerk components */}
                      {/* <Route path={routes.auth.getForgotPasswordRoute()} element={<ForgotPassword />} /> */}
                      <Route path={routes.auth.getSignInRoute()} element={<ClerkSignIn />} />
                      <Route path={`${routes.auth.getSignInRoute()}/*`} element={<ClerkSignIn />} />
                      <Route path={routes.auth.getSignUpRoute()} element={<ClerkSignUp />} />
                      <Route path={`${routes.auth.getSignUpRoute()}/*`} element={<ClerkSignUp />} />
                      {/* <Route path={routes.auth.getAccountSelectionRoute()} element={<AccountDetails />} /> */}

                      <Route path={routes.recipe.getImportRecipeDirectRoute()} element={<ImportRecipe />} />
                      <Route path={routes.widgets.getImportWidgetDirectRoute()} element={<ImportWidgetScreen />} />

                      <Route path={routes.getBaseRoute()} element={<AuthStateRouter />} />
                    </Route>
                    <Route path={'*'} element={<AuthStateRouter />} />
                  </Routes>
                </UserSessionContextProvider>
              </I18nProvider>
            </ApplicationContextProvider>
          </Router>
        </ClerkProvider>
        {/* </AntApp> */}
      </ThemeProvider>
    </div>
  );
}


export default App;
